import { useCallback, useEffect, useMemo, useState, type ReactNode } from "react";
import { Link } from "react-router-dom";
import clsx from "clsx";
import {
  ArrowUturnLeftIcon,
  CheckCircleIcon,
  CheckIcon,
  PaperAirplaneIcon,
  ShoppingCartIcon,
} from "@heroicons/react/24/outline";
import { SupplierLogo } from "@/components/GeschaeftspartnerLogo";
import { Dialog } from "@/components/ui/Dialog";
import { Button } from "@/components/ui/Button";
import { TextInput } from "@/components/ui/Field";
import { Spinner } from "@/components/ui/Spinner";
import {
  getAnfrageGroupComparison,
  saveAnfragePositionAngebot,
  type AnfrageDetail,
  type AnfrageGroupComparison,
  type AnfragePosition,
} from "@/lib/anfragen";
import { formatAuftragsnummerDisplay } from "@/lib/orderDraftUtils";
import { toast } from "@/lib/toast";
import {
  anfrageSendLabel,
  anfrageStage,
  canCloseAnfrage,
  canConvertAnfrage,
  fmtDateTime,
  fmtRelativeDays,
  positionAuftragDisplay,
  stageBadgeClass,
  stageLabel,
  type AnfrageStage,
  uniqueTaNummern,
} from "@/pages/anfragen/anfrageWizardUtils";
import { MailThreadSection } from "@/pages/anfragen/MailThreadSection";

/* ─────────────────────────── Preise pro Position ─────────────────────────── */

interface PositionOfferDraft {
  ek_preis: string;
  vk_preis: string;
  angebot_waehrung: string;
  angebot_gueltig_bis: string;
  angebot_datei_url: string;
  dirty: boolean;
}

function offerDraftFromPosition(p: AnfragePosition): PositionOfferDraft {
  return {
    ek_preis: p.ek_preis != null ? String(p.ek_preis) : p.angebot_preis != null ? String(p.angebot_preis) : "",
    vk_preis: p.vk_preis != null ? String(p.vk_preis) : "",
    angebot_waehrung: p.angebot_waehrung || "EUR",
    angebot_gueltig_bis: p.angebot_gueltig_bis
      ? (String(p.angebot_gueltig_bis).includes("T")
          ? String(p.angebot_gueltig_bis).split("T")[0]
          : String(p.angebot_gueltig_bis)) ?? ""
      : "",
    angebot_datei_url: p.angebot_datei_url ?? "",
    dirty: false,
  };
}

function offerDraftToBody(draft: PositionOfferDraft): Record<string, unknown> {
  const body: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(draft)) {
    if (k === "dirty") continue;
    const trimmed = String(v).trim().replace(",", ".");
    if (k === "ek_preis" || k === "vk_preis") {
      body[k] = trimmed === "" ? null : parseFloat(trimmed);
    } else {
      body[k] = trimmed === "" ? null : String(v).trim();
    }
  }
  return body;
}

function fmtPrice(value: number | null | undefined, currency?: string | null): string {
  if (value == null || !Number.isFinite(Number(value))) return "–";
  return `${Number(value).toLocaleString("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${currency || "EUR"}`;
}

function fmtDateOnly(value: string | null | undefined): string {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric" });
}

/* ─────────────────────────── Ablauf-Stepper ─────────────────────────── */

type StepState = "done" | "current" | "todo";

function ProgressSteps({
  stage,
  detail,
}: {
  stage: AnfrageStage;
  detail: AnfrageDetail;
}) {
  const orderedAt = detail.tk_links?.[0]?.created_at;
  const steps: { label: string; state: StepState; sub?: string }[] = [
    {
      label: "Erstellt",
      state: "done",
      sub: fmtDateTime(detail.created_at),
    },
    {
      label: "Versendet",
      state: stage === "ready" || stage === "draft" ? "current" : "done",
      sub:
        stage === "ready" || stage === "draft"
          ? "noch offen"
          : fmtDateTime(detail.last_sent_at || detail.updated_at),
    },
    {
      label: "Antwort",
      state:
        stage === "waiting"
          ? "current"
          : stage === "ready" || stage === "draft"
            ? "todo"
            : "done",
      sub:
        stage === "waiting"
          ? "wartet"
          : stage === "ready" || stage === "draft"
            ? undefined
            : detail.last_answer_at
              ? fmtDateTime(detail.last_answer_at)
              : stage === "ordered" || stage === "done"
                ? "übersprungen"
                : undefined,
    },
    {
      label: stage === "ordered" ? "Bestellt" : "Abgeschlossen",
      state: stage === "ordered" || stage === "done" ? "done" : stage === "answered" ? "current" : "todo",
      sub:
        stage === "done"
          ? fmtDateTime(detail.erledigt_at)
          : stage === "ordered"
            ? fmtDateTime(orderedAt)
            : stage === "answered"
              ? "bestellen oder erledigen"
              : undefined,
    },
  ];

  return (
    <ol className="grid grid-cols-4 gap-1" aria-label="Ablauf der Anfrage">
      {steps.map((s, idx) => (
        <li key={s.label} className="relative flex flex-col items-center text-center">
          {idx > 0 ? (
            <span
              className={clsx(
                "absolute end-1/2 top-3 h-0.5 w-full",
                s.state === "todo" ? "bg-separator" : "bg-accent/60",
              )}
              aria-hidden
            />
          ) : null}
          <span
            className={clsx(
              "relative z-10 inline-flex h-6 w-6 items-center justify-center rounded-full border-2 text-xs font-bold",
              s.state === "done" && "border-accent bg-accent text-label-on-accent",
              s.state === "current" && "border-accent bg-card text-accent ring-4 ring-accent-soft",
              s.state === "todo" && "border-separator-opaque bg-card text-label-tertiary",
            )}
          >
            {s.state === "done" ? <CheckIcon className="h-3.5 w-3.5" aria-hidden /> : idx + 1}
          </span>
          <span
            className={clsx(
              "mt-1.5 text-xs font-semibold",
              s.state === "todo" ? "text-label-tertiary" : "text-label",
            )}
          >
            {s.label}
          </span>
          {s.sub ? <span className="text-[11px] text-label-tertiary">{s.sub}</span> : null}
        </li>
      ))}
    </ol>
  );
}

/* ─────────────────────────── Nächster Schritt ─────────────────────────── */

function NextStepCard({
  stage,
  detail,
  pendingMailCount,
  sendLabel,
  statusBusy,
  onSend,
  onConvert,
  onMarkDone,
  onReopen,
  onScrollToAnswers,
  onScrollToPrices,
}: {
  stage: AnfrageStage;
  detail: AnfrageDetail;
  pendingMailCount: number;
  sendLabel: string;
  statusBusy: boolean;
  onSend?: () => void;
  onConvert: () => void;
  onMarkDone: () => void;
  onReopen: () => void;
  onScrollToAnswers: () => void;
  onScrollToPrices: () => void;
}) {
  const supplier = detail.supplier_name || "dem Lieferanten";
  const tkNums = (detail.tk_links || [])
    .map((l) => l.bestellnummern)
    .filter((v): v is string => !!v && v.trim() !== "");

  let tone = "border-accent/30 bg-accent-soft/40";
  let title = "";
  let text = "";
  let buttons: ReactNode = null;

  switch (stage) {
    case "ready":
      tone = "border-status-orange/40 bg-status-orange/10";
      title = "Noch nicht versendet";
      text = `Die Anfrage ist fertig vorbereitet, aber noch nicht bei ${supplier}. Beim Versenden kannst du die Mail noch anpassen und Anhänge hinzufügen.`;
      buttons = (
        <>
          {onSend ? (
            <Button type="button" onClick={onSend} leftIcon={<PaperAirplaneIcon className="h-4 w-4" aria-hidden />}>
              {sendLabel}
            </Button>
          ) : null}
          <Button type="button" variant="ghost" onClick={onMarkDone} disabled={statusBusy}>
            Nicht mehr nötig – erledigen
          </Button>
        </>
      );
      break;
    case "waiting": {
      const since = fmtRelativeDays(detail.last_sent_at || detail.updated_at);
      title = `Wartet auf Antwort von ${supplier}`;
      text = `Versendet ${since || "vor kurzem"}. Antworten aus dem Postfach erscheinen automatisch weiter unten. Kommt das Angebot auf anderem Weg, kannst du die Preise direkt erfassen.`;
      buttons = (
        <>
          <Button type="button" variant="secondary" onClick={onScrollToPrices}>
            Preise erfassen
          </Button>
          <Button type="button" variant="secondary" onClick={onConvert}>
            Bestellung anlegen
          </Button>
          <Button type="button" variant="ghost" onClick={onMarkDone} disabled={statusBusy}>
            Als erledigt markieren
          </Button>
        </>
      );
      break;
    }
    case "answered":
      tone = "border-status-green/40 bg-status-green/10";
      title =
        pendingMailCount > 0
          ? `${pendingMailCount} neue ${pendingMailCount === 1 ? "Antwort" : "Antworten"} von ${supplier}`
          : `Antwort von ${supplier} liegt vor`;
      text =
        pendingMailCount > 0
          ? "Antwort lesen und im Projektordner ablegen. Danach entscheiden: Bestellung auslösen – oder die Anfrage nur als erledigt markieren, wenn die Preise z. B. fürs Angebot reichen."
          : "Die Antwort ist abgelegt. Jetzt bestellen – oder als erledigt markieren, wenn keine Bestellung folgt.";
      buttons = (
        <>
          {pendingMailCount > 0 ? (
            <Button type="button" onClick={onScrollToAnswers}>
              Antwort ansehen
            </Button>
          ) : null}
          <Button
            type="button"
            variant={pendingMailCount > 0 ? "secondary" : "primary"}
            onClick={onConvert}
            leftIcon={<ShoppingCartIcon className="h-4 w-4" aria-hidden />}
          >
            In Bestellung umwandeln
          </Button>
          <Button
            type="button"
            variant="secondary"
            onClick={onMarkDone}
            disabled={statusBusy}
            leftIcon={<CheckCircleIcon className="h-4 w-4" aria-hidden />}
          >
            Als erledigt markieren
          </Button>
        </>
      );
      break;
    case "ordered":
      tone = "border-status-indigo/40 bg-status-indigo/10";
      title = tkNums.length ? `Bestellung ${tkNums.join(", ")} erzeugt` : "Bestellung erzeugt";
      text = "Aus dieser Anfrage wurde bereits bestellt. Wenn nichts mehr offen ist, kannst du sie als erledigt markieren.";
      buttons = (
        <>
          <Button type="button" variant="secondary" onClick={onMarkDone} disabled={statusBusy}>
            Als erledigt markieren
          </Button>
          <Button type="button" variant="ghost" onClick={onConvert}>
            Weitere Bestellung
          </Button>
        </>
      );
      break;
    case "done":
      tone = "border-separator bg-fill-quaternary/40";
      title = "Erledigt";
      text = `Abgeschlossen ${detail.erledigt_at ? `am ${fmtDateTime(detail.erledigt_at)}` : ""}${
        detail.erledigt_von ? ` von ${detail.erledigt_von}` : ""
      }. Es wurde keine Bestellung ausgelöst.`;
      buttons = (
        <>
          <Button
            type="button"
            variant="secondary"
            onClick={onReopen}
            disabled={statusBusy}
            leftIcon={<ArrowUturnLeftIcon className="h-4 w-4" aria-hidden />}
          >
            Wieder öffnen
          </Button>
          <Button type="button" variant="ghost" onClick={onConvert}>
            Doch bestellen
          </Button>
        </>
      );
      break;
    case "draft":
      title = "Entwurf";
      text = "Diese Anfrage ist noch nicht abgeschlossen. Öffne sie in der Liste über „Weiter bearbeiten“.";
      break;
  }

  return (
    <section className={clsx("rounded-ios border p-4", tone)} aria-label="Nächster Schritt">
      <p className="text-[11px] font-bold uppercase tracking-wide text-label-tertiary">Nächster Schritt</p>
      <h3 className="mt-0.5 text-base font-semibold text-label">{title}</h3>
      <p className="mt-1 text-sm text-label-secondary">{text}</p>
      {buttons ? <div className="mt-3 flex flex-wrap gap-2">{buttons}</div> : null}
    </section>
  );
}

/* ─────────────────────────── Dialog ─────────────────────────── */

export function AnfrageDetailDialog({
  open,
  detail,
  loading,
  mailCounts,
  statusBusy = false,
  onClose,
  onConvert,
  onRefresh,
  onReloadDetail,
  onSend,
  onMarkDone,
  onReopen,
  onOpenMailRead,
  onConfirmMailCandidate,
}: {
  open: boolean;
  detail: AnfrageDetail | null;
  loading: boolean;
  mailCounts: Record<string, number>;
  statusBusy?: boolean;
  onClose: () => void;
  onConvert: () => void;
  onRefresh: () => void;
  onReloadDetail: () => void;
  onSend?: () => void;
  onMarkDone: () => void;
  onReopen: () => void;
  onOpenMailRead?: (ref: { ref_type: string; ref_id?: number | string | null }) => void;
  onConfirmMailCandidate?: (candidateId: number) => void;
}) {
  const [comparison, setComparison] = useState<AnfrageGroupComparison | null>(null);
  const [offerDrafts, setOfferDrafts] = useState<Record<number, PositionOfferDraft>>({});
  const [savingPrices, setSavingPrices] = useState(false);

  useEffect(() => {
    if (!open || !detail?.anfrage_group_id) {
      setComparison(null);
      return;
    }
    void getAnfrageGroupComparison(detail.anfrage_group_id)
      .then(setComparison)
      .catch(() => setComparison(null));
  }, [open, detail?.anfrage_group_id]);

  useEffect(() => {
    if (!detail?.positionen) {
      setOfferDrafts({});
      return;
    }
    const next: Record<number, PositionOfferDraft> = {};
    for (const p of detail.positionen) {
      if (p.id != null) next[p.id] = offerDraftFromPosition(p);
    }
    setOfferDrafts(next);
  }, [detail?.id, detail?.positionen]);

  const pendingMailCount = detail ? mailCounts[String(detail.id)] ?? detail.pending_mail_count ?? 0 : 0;
  const stage: AnfrageStage = detail ? anfrageStage(detail, pendingMailCount) : "waiting";
  const taNums = detail ? uniqueTaNummern(detail) : [];
  const canConvert = detail ? canConvertAnfrage(detail) : false;
  const canClose = detail ? canCloseAnfrage(detail) : false;
  const groupSize = detail?.group_siblings ? detail.group_siblings.length + 1 : 0;
  const sendLabel = anfrageSendLabel(groupSize > 1 ? groupSize : 1);
  const dirtyCount = useMemo(
    () => Object.values(offerDrafts).filter((d) => d.dirty).length,
    [offerDrafts],
  );

  const auftragLabels = useMemo(() => {
    if (!detail) return [] as string[];
    const list = (detail.auftraege || [])
      .map((a) => a.auftragsnummer_display || formatAuftragsnummerDisplay(a) || a.auftragsnummer || "")
      .filter(Boolean);
    if (list.length) return list;
    return [...new Set((detail.positionen || []).map((p) => positionAuftragDisplay(p)).filter(Boolean))];
  }, [detail]);

  const handleMailThreadRefresh = useCallback(() => {
    onRefresh();
    onReloadDetail();
  }, [onRefresh, onReloadDetail]);

  function updateOffer(pid: number, patch: Partial<PositionOfferDraft>) {
    setOfferDrafts((prev) => {
      const base = prev[pid] ?? offerDraftFromPosition({ id: pid } as AnfragePosition);
      return { ...prev, [pid]: { ...base, ...patch, dirty: true } };
    });
  }

  async function saveAllPrices() {
    if (!detail) return;
    const dirty = Object.entries(offerDrafts).filter(([, d]) => d.dirty);
    if (!dirty.length) return;
    setSavingPrices(true);
    try {
      for (const [pidRaw, draft] of dirty) {
        await saveAnfragePositionAngebot(detail.id, Number(pidRaw), offerDraftToBody(draft));
      }
      toast.success(dirty.length === 1 ? "Preis gespeichert." : `${dirty.length} Preise gespeichert.`);
      onReloadDetail();
    } catch {
      toast.error("Preise konnten nicht gespeichert werden.");
    } finally {
      setSavingPrices(false);
    }
  }

  function scrollTo(id: string) {
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
  }

  const supplierLine = detail
    ? [detail.supplier_contact_name, detail.supplier_email].filter(Boolean).join(" · ")
    : "";

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={
        detail ? (
          <span className="flex flex-wrap items-center gap-2">
            <SupplierLogo
              name={detail.supplier_name}
              partnerId={detail.supplier_gp_id}
              hasLogo={detail.supplier_has_logo}
              logoUpdatedAt={detail.supplier_logo_updated_at}
              className="h-7 w-7 rounded-md text-[10px]"
            />
            <span>{detail.supplier_name || "Anfrage"}</span>
            <span className={clsx("rounded-full px-2 py-0.5 text-xs font-semibold", stageBadgeClass(stage))}>
              {stageLabel(stage)}
            </span>
          </span>
        ) : (
          "Anfrage"
        )
      }
      description={detail ? [taNums.join(", "), detail.subject].filter(Boolean).join(" · ") : undefined}
      size="2xl"
      zIndexClass="z-[105]"
      fullScreenMobile
      footer={
        <footer className="flex w-full flex-wrap items-center justify-between gap-2">
          <Button type="button" variant="secondary" onClick={onClose}>
            Schließen
          </Button>
          {detail ? (
            <div className="flex flex-wrap justify-end gap-2">
              {stage === "done" ? (
                <Button type="button" variant="secondary" onClick={onReopen} disabled={statusBusy}>
                  Wieder öffnen
                </Button>
              ) : canClose ? (
                <Button type="button" variant="secondary" onClick={onMarkDone} disabled={statusBusy}>
                  Als erledigt markieren
                </Button>
              ) : null}
              {stage === "ready" && onSend && (detail.supplier_email || detail.anfrage_group_id) ? (
                <Button type="button" onClick={onSend}>
                  {sendLabel}
                </Button>
              ) : (
                <Button
                  type="button"
                  variant={stage === "answered" ? "primary" : "secondary"}
                  onClick={onConvert}
                  disabled={!canConvert}
                >
                  In Bestellung umwandeln
                </Button>
              )}
            </div>
          ) : null}
        </footer>
      }
    >
      {loading && !detail ? (
        <div className="flex justify-center py-12">
          <Spinner />
        </div>
      ) : detail ? (
        <div className="space-y-4">
          <ProgressSteps stage={stage} detail={detail} />

          <NextStepCard
            stage={stage}
            detail={detail}
            pendingMailCount={pendingMailCount}
            sendLabel={sendLabel}
            statusBusy={statusBusy}
            onSend={onSend && (detail.supplier_email || detail.anfrage_group_id) ? onSend : undefined}
            onConvert={onConvert}
            onMarkDone={onMarkDone}
            onReopen={onReopen}
            onScrollToAnswers={() => scrollTo("anfrage-mail-verlauf")}
            onScrollToPrices={() => scrollTo("anfrage-positionen")}
          />

          <section className="grid gap-x-4 gap-y-2 rounded-ios border border-separator bg-card p-3 text-sm sm:grid-cols-2">
            <div className="flex min-w-0 items-center gap-2">
              <SupplierLogo
                name={detail.supplier_name}
                partnerId={detail.supplier_gp_id}
                hasLogo={detail.supplier_has_logo}
                logoUpdatedAt={detail.supplier_logo_updated_at}
                className="h-9 w-9 rounded-lg text-[11px]"
              />
              <div className="min-w-0">
                <span className="text-xs text-label-secondary">Lieferant</span>
                <p className="truncate font-medium text-label">{detail.supplier_name || "–"}</p>
                {supplierLine ? <p className="truncate text-xs text-label-secondary">{supplierLine}</p> : null}
              </div>
            </div>
            <div className="min-w-0">
              <span className="text-xs text-label-secondary">
                {auftragLabels.length === 1 ? "Auftrag" : "Aufträge"}
              </span>
              <p className="truncate font-medium text-label">{auftragLabels.join(", ") || "–"}</p>
              {taNums.length ? <p className="truncate font-mono text-xs text-label-secondary">{taNums.join(", ")}</p> : null}
            </div>
            <div className="flex flex-wrap gap-2 sm:col-span-2">
              {detail.anfrage_group_id && groupSize > 1 ? (
                <span className="rounded-full bg-fill-tertiary px-2.5 py-0.5 text-xs font-medium text-label-secondary">
                  Gleiche Anfrage an {groupSize} Firmen
                </span>
              ) : null}
              {detail.source_ma?.id ? (
                <Link
                  to={`/materialanforderungen?id=${detail.source_ma.id}`}
                  className="rounded-full bg-fill-tertiary px-2.5 py-0.5 text-xs font-medium text-accent hover:underline"
                >
                  Aus Materialanforderung {detail.source_ma.ma_nummer || `#${detail.source_ma.id}`}
                </Link>
              ) : null}
              {(detail.tk_links || []).map((l) => (
                <Link
                  key={l.order_id}
                  to={`/bestellungen?id=${l.order_id}`}
                  className="rounded-full bg-status-indigo/10 px-2.5 py-0.5 font-mono text-xs font-semibold text-status-indigo hover:underline"
                >
                  {l.bestellnummern || `Bestellung #${l.order_id}`}
                </Link>
              ))}
              {detail.created_by ? (
                <span className="rounded-full bg-fill-tertiary px-2.5 py-0.5 text-xs text-label-secondary">
                  Angelegt von {detail.created_by}
                </span>
              ) : null}
            </div>
          </section>

          <section
            id="anfrage-positionen"
            className="rounded-ios border border-separator bg-card p-3 scroll-mt-4"
            aria-label="Positionen und Preise"
          >
            <header className="mb-2 flex flex-wrap items-center justify-between gap-2">
              <h3 className="text-sm font-semibold text-label">
                Positionen &amp; Preise
                <span className="ms-1.5 font-normal text-label-secondary">({(detail.positionen || []).length})</span>
              </h3>
              <p className="text-xs text-label-tertiary">
                EK = Preis vom Lieferanten, VK = unser Verkaufspreis. Optional.
              </p>
            </header>
            <ul className="divide-y divide-separator/60" role="list">
              {(detail.positionen || []).map((p) => {
                const pid = p.id;
                const draft = pid != null ? offerDrafts[pid] : null;
                return (
                  <li key={pid ?? `${p.auftrag_id}-${p.text}`} className="py-2.5 first:pt-0 last:pb-0">
                    <div className="flex flex-wrap items-start justify-between gap-x-4 gap-y-2">
                      <div className="min-w-0 flex-1">
                        <p className="font-medium text-label">{p.text || "–"}</p>
                        <p className="mt-0.5 text-xs text-label-secondary">
                          {p.menge} {p.einheit || ""}
                          {auftragLabels.length > 1 ? ` · ${positionAuftragDisplay(p)}` : ""}
                          {p.hero_nr ? ` · Art.-Nr. ${p.hero_nr}` : ""}
                        </p>
                        {!draft?.dirty && (p.ek_preis != null || p.vk_preis != null) ? (
                          <p className="mt-0.5 text-xs text-label-secondary">
                            EK {fmtPrice(p.ek_preis ?? p.angebot_preis, p.angebot_waehrung)}
                            {p.vk_preis != null ? ` · VK ${fmtPrice(p.vk_preis, p.angebot_waehrung)}` : ""}
                            {p.angebot_gueltig_bis ? ` · gültig bis ${fmtDateOnly(p.angebot_gueltig_bis)}` : ""}
                          </p>
                        ) : null}
                      </div>
                      {pid != null && draft ? (
                        <div className="flex flex-wrap items-end gap-2">
                          <TextInput
                            label="EK"
                            inputMode="decimal"
                            inputClassName="!w-24"
                            placeholder="0,00"
                            value={draft.ek_preis}
                            onChange={(e) => updateOffer(pid, { ek_preis: e.target.value })}
                          />
                          <TextInput
                            label="VK"
                            inputMode="decimal"
                            inputClassName="!w-24"
                            placeholder="0,00"
                            value={draft.vk_preis}
                            onChange={(e) => updateOffer(pid, { vk_preis: e.target.value })}
                          />
                          <details className="group relative">
                            <summary className="cursor-pointer select-none list-none rounded-lg px-2 py-2 text-xs text-accent hover:bg-fill-tertiary">
                              Mehr
                            </summary>
                            <div className="mt-2 flex flex-wrap gap-2 rounded-ios border border-separator bg-card-elevated p-2 sm:absolute sm:end-0 sm:z-10 sm:min-w-[22rem] sm:shadow-ios-md">
                              <TextInput
                                label="Währung"
                                inputClassName="!w-20"
                                value={draft.angebot_waehrung}
                                onChange={(e) => updateOffer(pid, { angebot_waehrung: e.target.value })}
                              />
                              <TextInput
                                label="Gültig bis"
                                type="date"
                                inputClassName="!w-36"
                                value={draft.angebot_gueltig_bis}
                                onChange={(e) => updateOffer(pid, { angebot_gueltig_bis: e.target.value })}
                              />
                              <TextInput
                                label="Link zum Angebot"
                                className="min-w-[10rem] flex-1"
                                value={draft.angebot_datei_url}
                                onChange={(e) => updateOffer(pid, { angebot_datei_url: e.target.value })}
                              />
                            </div>
                          </details>
                        </div>
                      ) : null}
                    </div>
                  </li>
                );
              })}
            </ul>
            {dirtyCount > 0 ? (
              <div className="mt-3 flex items-center justify-end gap-2 border-t border-separator pt-3">
                <span className="text-xs text-label-secondary">
                  {dirtyCount === 1 ? "1 Position geändert" : `${dirtyCount} Positionen geändert`}
                </span>
                <Button type="button" size="sm" loading={savingPrices} onClick={() => void saveAllPrices()}>
                  Preise speichern
                </Button>
              </div>
            ) : null}
          </section>

          {comparison && (comparison.comparison_rows || []).length ? (
            <section className="overflow-x-auto rounded-ios border border-separator bg-card p-3">
              <h3 className="mb-2 text-sm font-semibold text-label">Preisvergleich der Firmen</h3>
              <table className="w-full min-w-[28rem] text-sm">
                <thead>
                  <tr className="border-b border-separator text-left text-xs text-label-secondary">
                    <th className="pb-2 pr-3">Position</th>
                    {(comparison.suppliers || []).map((s) => (
                      <th key={s.anfrage_id} className="pb-2 pr-3">
                        <span className="inline-flex items-center gap-1.5">
                          <SupplierLogo
                            name={s.supplier_name}
                            partnerId={s.supplier_gp_id}
                            hasLogo={s.supplier_has_logo}
                            logoUpdatedAt={s.supplier_logo_updated_at}
                            className="h-5 w-5 rounded text-[8px]"
                          />
                          {s.supplier_name || "?"}
                        </span>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {(comparison.comparison_rows || []).map((row, idx) => (
                    <tr key={idx} className="border-b border-separator/50">
                      <td className="py-2 pr-3">
                        <div className="font-medium">{row.text || ""}</div>
                        <div className="text-xs text-label-secondary">
                          {row.menge != null ? `${row.menge} ${row.einheit || ""}` : ""}
                        </div>
                      </td>
                      {(comparison.suppliers || []).map((s) => {
                        const v = row.by_supplier?.[String(s.anfrage_id)];
                        return (
                          <td key={s.anfrage_id} className="py-2 pr-3">
                            <span className={clsx("font-semibold", v?.ek_preis != null ? "text-label" : "text-label-tertiary")}>
                              {fmtPrice(v?.ek_preis, v?.angebot_waehrung)}
                            </span>
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </section>
          ) : null}

          <div id="anfrage-mail-verlauf" className="scroll-mt-4">
            <MailThreadSection
              anfrageId={detail.id}
              onOpenMailRead={onOpenMailRead ?? (() => {})}
              onConfirmCandidate={onConfirmMailCandidate ?? (() => {})}
              onDismissCandidate={handleMailThreadRefresh}
              onRefresh={handleMailThreadRefresh}
            />
          </div>

          <details className="rounded-ios border border-separator bg-card">
            <summary className="cursor-pointer select-none px-3 py-2.5 text-sm font-semibold text-label">
              Text der Anfrage
              <span className="ms-2 font-normal text-label-tertiary">{detail.subject || ""}</span>
            </summary>
            <div className="border-t border-separator px-3 py-3">
              <p className="mb-2 text-xs text-label-secondary">Betreff</p>
              <p className="mb-3 text-sm text-label">{detail.subject || "–"}</p>
              <p className="mb-1 text-xs text-label-secondary">Text</p>
              <pre className="whitespace-pre-wrap rounded-ios bg-fill-quaternary/40 p-3 font-sans text-sm text-label">
                {detail.body || "–"}
              </pre>
            </div>
          </details>
        </div>
      ) : (
        <p className="text-sm text-label-secondary">Anfrage nicht gefunden.</p>
      )}
    </Dialog>
  );
}
